import { equals } from 'ramda';

import { Days, FormatDaysOptions } from './definitions';

export const formatTime = (time: string, options: FormatDaysOptions): string => {
  const { noon, midnight } = options;
  if (!time) {
    return null;
  }

  if (time.toLowerCase() === '12am') {
    return midnight;
  }

  if (time.toLowerCase() === '12pm') {
    return noon;
  }

  return time.toLowerCase();
};

const daysKeys = Object.keys(Days).map((s) => s.toLowerCase());

/**
 * If it is not Monday-Sunday
 */
export const isPublicHoliday = (dayName: string): boolean =>
  daysKeys.indexOf(dayName.toLowerCase()) < 0;

export const normaliseDays = (days, options: FormatDaysOptions) => days.map((day) => (
  {
    id: day?.id,
    name: day?.title?.value,
    alwaysOpen: Boolean(day?.alwaysOpen?.value) || false,
    times:
      day?.times
        .map((time) => (
          {
            id: time?.id,
            open: formatTime(time?.open?.value, options),
            close: formatTime(time?.close?.value, options)
          }
        ))
        .filter(({ open, close }) => open !== null && close !== null) || []
  }
));
;

const removeIdForCompare = (time) => (
  {
    open: time.open,
    close: time.close
  }
);

const defaultFormatDaysOptions: FormatDaysOptions = {
  noon: '12 noon',
  midnight: '12 midnight'
};

export const formatDays = (days, options: FormatDaysOptions) => {
  const mergedOptions: FormatDaysOptions = {
    ...defaultFormatDaysOptions,
    ...options
  };

  const groupedDays = [];
  const daysToSkip = [];

  normaliseDays(days, mergedOptions).forEach((day, i, array) => {
    if (daysToSkip.indexOf(day.name) !== -1) {
      // this day is already grouped so we can skip it
      return;
    }

    let nextDayIndex = i + 1;
    let loop = true;

    const data = {
      id: day.id,
      name: [day.name],
      alwaysOpen: day.alwaysOpen,
      times: day.times
    };

    // is public holiday or other day
    if (isPublicHoliday(day.name)) {
      groupedDays.push(data);

      return;
    }

    while (loop) {
      const nextDay = array[nextDayIndex];

      // no more days left
      if (typeof nextDay === 'undefined' || isPublicHoliday(nextDay.name)) {
        break;
      }

      if (
        day.alwaysOpen === nextDay.alwaysOpen &&
        equals(day.times.map(removeIdForCompare), nextDay.times.map(removeIdForCompare))
      ) {
        data.name.push(nextDay.name);
        nextDayIndex++;

        daysToSkip.push(nextDay.name);
      } else {
        loop = false;
      }
    }

    groupedDays.push(data);
  });

  return groupedDays;
};
